import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Grid, IconButton, InputAdornment, TextField, Typography, Collapse, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Agenda from './Agenda';
import AddPopupComponent from './AddPopupComponent';

const VV = { primary: '#3F2B96', secondary: '#7F5AF0', accent: '#00C896', bg: '#F7F8FC', border: 'rgba(63,43,150,0.10)', shadow: '0 8px 24px rgba(0,0,0,0.08)' };

const AgendaCard = ({ agenda, agendas, setAgendas, index, removeAgenda, repId, selectedSection }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card sx={{ borderRadius: '14px', border: `1.5px solid ${open ? VV.secondary : VV.border}`, boxShadow: open ? `0 8px 32px rgba(63,43,150,0.13)` : '0 2px 8px rgba(0,0,0,0.05)', mb: 2, transition: 'all 0.25s', overflow: 'hidden' }}>
      <Box onClick={() => setOpen(o => !o)}
        sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 2, cursor: 'pointer', bgcolor: open ? `${VV.primary}06` : '#fff', transition: 'background 0.2s', '&:hover': { bgcolor: `${VV.primary}06` } }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: open ? `linear-gradient(135deg, ${VV.primary}, ${VV.secondary})` : `${VV.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.25s', flexShrink: 0 }}>
          <AssignmentIcon sx={{ fontSize: 18, color: open ? '#fff' : VV.primary }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: VV.primary, fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {agenda.topic || `Agenda ${index + 1}`}
          </Typography>
          {agenda.detail && !open && (
            <Typography variant="caption" sx={{ color: '#888', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{agenda.detail}</Typography>
          )}
        </Box>
        <Chip label={`#${index + 1}`} size="small" sx={{ bgcolor: `${VV.secondary}15`, color: VV.secondary, fontWeight: 700, borderRadius: '8px', fontSize: '0.7rem' }} />
        <IconButton size="small" sx={{ color: open ? VV.secondary : '#aaa', transition: 'color 0.2s' }}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Divider sx={{ borderColor: VV.border }} />
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#fafcff' }}>
          <Agenda agenda={agenda} agendas={agendas} setAgendas={setAgendas} index={index} removeAgenda={removeAgenda} repId={repId} />
        </Box>
      </Collapse>
    </Card>
  );
};

function AgendaEditTab({ agendas, setAgendas, removeAgenda, repId, selectedSection, updateAgendasDB }) {
  const [searchTerm, setSearchTerm] = useState('');

  // Remove the problematic useEffect that was calling updateAgendasDB on every agendas change
  // This was causing too many API calls

  const filtered = (agendas || []).filter(a =>
    !searchTerm || (a.topic || '').toLowerCase().includes(searchTerm.toLowerCase()) || (a.detail || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ bgcolor: VV.bg, minHeight: '60vh', pb: 4 }}>
      {/* Header */}
      <Box sx={{ background: `linear-gradient(135deg, ${VV.primary} 0%, ${VV.secondary} 100%)`, pt: 4, pb: 8, px: { xs: 2, md: 5 } }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: 2 }}>Representative</Typography>
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, mb: 0.5 }}>Agenda Management</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem' }}>{(agendas || []).length} agenda item{(agendas || []).length !== 1 ? 's' : ''}</Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 }, mt: -5 }}>
        {/* Toolbar */}
        <Card sx={{ borderRadius: '14px', boxShadow: '0 12px 40px rgba(63,43,150,0.15)', mb: 3 }}>
          <CardContent sx={{ p: 2.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" placeholder="Search agendas..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#aaa', fontSize: 18 }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            <AddPopupComponent setData={setAgendas} data={agendas} title="Add Agenda" selectedSection={selectedSection} />
          </CardContent>
        </Card>

        {/* List */}
        {filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <AssignmentIcon sx={{ fontSize: 64, color: '#ddd', mb: 2, display: 'block', mx: 'auto' }} />
            <Typography color="text.secondary">{searchTerm ? 'No agendas match your search.' : 'No agenda items yet.'}</Typography>
          </Box>
        ) : (
          filtered.map((agenda, index) => (
            <AgendaCard key={index} agenda={agenda} agendas={agendas} setAgendas={setAgendas}
              index={index} removeAgenda={removeAgenda} repId={repId} selectedSection={selectedSection} />
          ))
        )}
      </Box>
    </Box>
  );
}

export default AgendaEditTab;
